import { useEffect, useMemo, useState } from "react"
import { useFormik } from "formik"
import { Button, Col, Form, Input, Label, Row } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  buildCreatableOptions,
  buildCustomerTypeOptions, getCustomerTypeFieldMeta,
  resolveCustomerTypeKey
} from "helpers/customerOnboarding"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useMoneyReceiptBlueprint } from "hooks/useMoneyReceipt"
import "./MoneyReceiptAddEdit.css"

// ── helpers ────────────────────────────────────────────────────────────────
const getDisplayText = value => {
  if (!value) return ""
  if (typeof value === "string") return value
  if (typeof value === "object")
    return value?.name || value?.title || value?.label || ""
  return String(value)
}

const buildVehicleName = vehicle =>
  [
    getDisplayText(vehicle?.brand),
    getDisplayText(vehicle?.model),
    getDisplayText(vehicle?.package),
    getDisplayText(vehicle?.engine_type),
  ]
    .filter(Boolean)
    .join(" ")

const normalizeCustomerTypeValue = value => resolveCustomerTypeKey(value)

const getCustomerOrganizationLabel = customerType => {
  if (customerType === "banker") return "Bank"
  if (customerType === "service") return "Company"
  return "Organization"
}

const createEmptyAdditionalCost = () => ({ name: "", amount: "" })

const createEmptyPayment = () => ({
  received_date: new Date().toISOString().split("T")[0],
  payment_method: "",
  amount: "",
  notes: "",
})

const normalizePayments = payments =>
  (payments || []).map(p => ({
    received_date: p?.received_date?.slice?.(0, 10) ?? new Date().toISOString().split("T")[0],
    payment_method: p?.paymentMethod?.name ?? p?.payment_method ?? "",
    amount: p?.amount ?? "",
    notes: p?.notes ?? p?.reference_number ?? "",
  }))

const getPaymentsTotal = payments =>
  (payments || []).reduce((sum, p) => sum + Number(p?.amount || 0), 0)

const normalizeAdditionalCosts = costs =>
  (costs || []).map(item => ({
    name: item?.name || "",
    amount: item?.amount ?? "",
  }))

const getAdditionalCostsTotal = costs =>
  (costs || []).reduce((total, item) => total + Number(item?.amount || 0), 0)

const formatAmount = value =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })

const buildMrDefaults = mr => ({
  issued_date:
    mr?.issued_date?.slice?.(0, 10) ?? new Date().toISOString().split("T")[0],
  car_price: mr?.car_price ?? "",
  registration_cost: mr?.registration_cost ?? 0,
  notes: mr?.notes ?? "",
  payments: normalizePayments(mr?.payments?.length ? mr.payments : [createEmptyPayment()]),
  pdf_content: {
    customer_info: {
      name: mr?.pdf_content?.customer_info?.name ?? mr?.customer?.name ?? "",
      mobile:
        mr?.pdf_content?.customer_info?.mobile ?? mr?.customer?.mobile ?? "",
      email:
        mr?.pdf_content?.customer_info?.email ?? mr?.customer?.email ?? "",
      customer_type:
        normalizeCustomerTypeValue(
          mr?.pdf_content?.customer_info?.customer_type ??
            mr?.customer?.customer_type
        ) || "general",
      designation:
        mr?.pdf_content?.customer_info?.designation ??
        mr?.customer?.designation ??
        "",
      company_name:
        mr?.pdf_content?.customer_info?.company_name ??
        (mr?.customer?.customer_type === "service"
          ? mr?.customer?.company_name
          : "") ??
        "",
      bank_name:
        mr?.pdf_content?.customer_info?.bank_name ??
        (mr?.customer?.customer_type === "banker"
          ? mr?.customer?.bank_name
          : "") ??
        "",
      branch_name:
        mr?.pdf_content?.customer_info?.branch_name ??
        (mr?.customer?.customer_type === "banker"
          ? mr?.customer?.branch_name
          : "") ??
        "",
      address:
        mr?.pdf_content?.customer_info?.address ??
        mr?.customer?.full_address ??
        "",
    },
    vehicle_info: {
      full_chassis:
        mr?.pdf_content?.vehicle_info?.full_chassis ??
        mr?.vehicle?.full_chassis ??
        "",
      full_engine:
        mr?.pdf_content?.vehicle_info?.full_engine ??
        mr?.vehicle?.full_engine ??
        "",
      name:
        mr?.pdf_content?.vehicle_info?.name ??
        buildVehicleName(mr?.vehicle) ??
        "",
      chassis_no:
        mr?.pdf_content?.vehicle_info?.chassis_no ??
        mr?.vehicle?.full_chassis ??
        mr?.vehicle?.chassis_number ??
        "",
      brta_reg:
        mr?.pdf_content?.vehicle_info?.brta_reg ?? mr?.vehicle?.brta_reg_no ?? "",
      year_model:
        mr?.pdf_content?.vehicle_info?.year_model ??
        getDisplayText(mr?.vehicle?.year) ??
        "",
      color:
        mr?.pdf_content?.vehicle_info?.color ??
        getDisplayText(mr?.vehicle?.exterior_color) ??
        "",
      cc:
        mr?.pdf_content?.vehicle_info?.cc ??
        getDisplayText(mr?.vehicle?.engine_c_c) ??
        "",
      fuel:
        mr?.pdf_content?.vehicle_info?.fuel ??
        getDisplayText(mr?.vehicle?.fuel_type) ??
        "",
      discount:
        mr?.pdf_content?.vehicle_info?.discount ??
        mr?.vehicle?.discount_price ??
        "",
    },
    additional_costs: normalizeAdditionalCosts(
      mr?.pdf_content?.additional_costs?.length
        ? mr?.pdf_content?.additional_costs
        : [createEmptyAdditionalCost()]
    ),
  },
})

const buildInitialValues = (mr, blueprintData = null) => {
  const base = buildMrDefaults(mr)
  if (!blueprintData) return base

  return {
    ...base,
    issued_date: blueprintData.issued_date ?? base.issued_date,
    car_price: blueprintData.car_price ?? base.car_price,
    registration_cost: blueprintData.registration_cost ?? base.registration_cost,
    notes: blueprintData.notes ?? base.notes,
    pdf_content: {
      ...base.pdf_content,
      ...(blueprintData.pdf_content ?? {}),
      additional_costs: normalizeAdditionalCosts(
        blueprintData.pdf_content?.additional_costs?.length
          ? blueprintData.pdf_content.additional_costs
          : [createEmptyAdditionalCost()]
      ),
    },
    payments: normalizePayments(
      blueprintData.payments?.length
        ? blueprintData.payments
        : [createEmptyPayment()]
    ),
  }
}

// ── Component ──────────────────────────────────────────────────────────────
const CustomReceiptModal = ({
  isOpen = false,
  toggle = () => {},
  moneyReceipt = null,
  onSubmit = () => {},
  isSubmitting = false,
}) => {
  const { data: blueprint, isLoading: blueprintLoading } = useMoneyReceiptBlueprint(
    moneyReceipt?.id,
    isOpen && !!moneyReceipt?.id
  )

  const { raw: filterOptionData, isLoading: customerLookupLoading } =
    useFilteredVehicleOptions()

  const customerLookupData = useMemo(() => {
    const raw = filterOptionData?.customer ?? {}
    return {
      customer_types: raw?.customer_types ?? [],
      designations: raw?.designations ?? [],
      companies: raw?.companies ?? [],
      banks: raw?.banks ?? [],
      branches: raw?.branches ?? [],
    }
  }, [filterOptionData?.customer])

  const customerTypeSelectOptions = useMemo(
    () => buildCustomerTypeOptions(customerLookupData.customer_types),
    [customerLookupData.customer_types]
  )
  const designationSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData.designations),
    [customerLookupData.designations]
  )
  const companySelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData.companies),
    [customerLookupData.companies]
  )
  const bankSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData.banks),
    [customerLookupData.banks]
  )
  const branchSelectOptions = useMemo(
    () => buildCreatableOptions(customerLookupData.branches),
    [customerLookupData.branches]
  )

  const latestBlueprint = blueprint?.[0] ?? null

  const [loadedBlueprintId, setLoadedBlueprintId] = useState(null)

  const initialValues = useMemo(
    () => buildInitialValues(moneyReceipt, latestBlueprint?.data ?? null),
    [moneyReceipt, isOpen, latestBlueprint?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: values => {
      const payload = {
        ...values,
        print_date: values.issued_date || null,
        pdf_content: {
          ...values.pdf_content,
          additional_costs: (values.pdf_content?.additional_costs || []).filter(
            item =>
              String(item?.name || "").trim() ||
              String(item?.amount || "").trim()
          ),
        },
      }
      onSubmit(payload)
    },
  })

  useEffect(() => {
    if (isOpen) {
      formik.resetForm({ values: buildInitialValues(moneyReceipt, latestBlueprint?.data ?? null) })
      setLoadedBlueprintId(latestBlueprint?.id ?? null)
    }
  }, [isOpen, latestBlueprint])

  const handleLoadBlueprint = bp => {
    formik.resetForm({ values: buildInitialValues(moneyReceipt, bp.data) })
    setLoadedBlueprintId(bp.id)
  }

  const handleResetToMrDefaults = () => {
    formik.resetForm({ values: buildMrDefaults(moneyReceipt) })
    setLoadedBlueprintId(null)
  }

  const selectedCustomerType = normalizeCustomerTypeValue(
    resolveCustomerTypeKey(
      formik.values.pdf_content?.customer_info?.customer_type
    )
  )
  const selectedCustomerTypeMeta = getCustomerTypeFieldMeta(selectedCustomerType)
  const isServiceCustomer = selectedCustomerType === "service"
  const isBankerCustomer = selectedCustomerType === "banker"

  const additionalCosts = formik.values.pdf_content?.additional_costs || []
  const payments = formik.values.payments || []
  const additionalCostsTotal = getAdditionalCostsTotal(additionalCosts)
  const totalAmount =
    Number(formik.values.car_price || 0) +
    Number(formik.values.registration_cost || 0) +
    additionalCostsTotal
  const paymentsTotal = getPaymentsTotal(payments)
  const dueAmount = Math.max(0, totalAmount - paymentsTotal)
  const overpaidAmount = Math.max(0, paymentsTotal - totalAmount)

  const handleCustomerTypeChange = value => {
    const nextTypeKey = resolveCustomerTypeKey(value)
    formik.setFieldValue("pdf_content.customer_info.customer_type", value)
    if (nextTypeKey === "general") {
      formik.setFieldValue("pdf_content.customer_info.designation", "")
      formik.setFieldValue("pdf_content.customer_info.company_name", "")
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
    } else if (nextTypeKey === "service") {
      formik.setFieldValue("pdf_content.customer_info.bank_name", "")
      formik.setFieldValue("pdf_content.customer_info.branch_name", "")
    } else {
      formik.setFieldValue("pdf_content.customer_info.company_name", "")
    }
  }

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Custom Money Receipt PDF"
      size="lg"
      footer={
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={formik.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="mdi mdi-loading mdi-spin me-1" />
                Generating...
              </>
            ) : (
              <>
                <i className="mdi mdi-file-pdf-box me-1" />
                Generate PDF
              </>
            )}
          </Button>
        </div>
      }
    >
      <Form onSubmit={formik.handleSubmit}>
        {blueprintLoading && isOpen && (
          <div className="text-center py-2 text-muted small">
            <i className="mdi mdi-loading mdi-spin me-1" />
            Loading blueprint history...
          </div>
        )}
        {!blueprintLoading && blueprint?.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <small className="fw-semibold text-muted">
                <i className="mdi mdi-history me-1" />
                Blueprint History ({blueprint.length} version{blueprint.length !== 1 ? "s" : ""})
              </small>
              <Button
                type="button"
                size="sm"
                color="secondary"
                outline
                onClick={handleResetToMrDefaults}
              >
                <i className="mdi mdi-refresh me-1" />
                Reset to MR Defaults
              </Button>
            </div>
            <div className="border rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
              {blueprint.map((bp, index) => (
                <div
                  key={bp.id}
                  className={`d-flex align-items-center gap-2 px-3 py-2${bp.id === loadedBlueprintId ? " bg-primary bg-opacity-10" : index % 2 === 0 ? " bg-light" : ""}`}
                  style={index < blueprint.length - 1 ? { borderBottom: "1px solid #dee2e6" } : undefined}
                >
                  <span className="badge bg-secondary" style={{ minWidth: "28px" }}>
                    v{blueprint.length - index}
                  </span>
                  <span className="small flex-grow-1 text-muted">
                    {bp.created_at ? new Date(bp.created_at).toLocaleString() : "—"}
                    {bp.created_by ? ` · ${bp.created_by}` : ""}
                  </span>
                  <div className="d-flex gap-1">
                    {bp.id === loadedBlueprintId ? (
                      <span className="badge bg-primary align-self-center">Loaded</span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        color="primary"
                        outline
                        disabled={isSubmitting}
                        onClick={() => handleLoadBlueprint(bp)}
                      >
                        Load
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      color="success"
                      outline
                      disabled={isSubmitting}
                      onClick={() => onSubmit({ ...bp.data, print_date: bp.data?.issued_date ?? bp.data?.print_date ?? null })}
                    >
                      <i className={`mdi ${isSubmitting ? "mdi-loading mdi-spin" : "mdi-file-pdf-box"}`} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <Row className="g-3">
          {/* ── Receipt Date ─────────────────────────────────── */}
          <Col md={4}>
            <Label className="form-label">Receipt Date</Label>
            <Input
              type="date"
              name="issued_date"
              value={formik.values.issued_date}
              onChange={formik.handleChange}
            />
          </Col>

          {/* ── CUSTOMER SECTION ─────────────────────────────── */}
          <Col md={12}>
            <div className="section-header">
              <h6>CUSTOMER INFORMATION</h6>
              <hr />
            </div>
          </Col>

          <Col md={12}>
            <div className="editor-panel">
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Label className="form-label">Name</Label>
                  <Input
                    name="pdf_content.customer_info.name"
                    value={formik.values.pdf_content?.customer_info?.name || ""}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="form-label">Mobile</Label>
                  <Input
                    name="pdf_content.customer_info.mobile"
                    value={
                      formik.values.pdf_content?.customer_info?.mobile || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="form-label">Email</Label>
                  <Input
                    name="pdf_content.customer_info.email"
                    value={
                      formik.values.pdf_content?.customer_info?.email || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <FormikCreatableSelect
                    name="pdf_content.customer_info.customer_type"
                    label="Customer Type"
                    formik={formik}
                    options={customerTypeSelectOptions}
                    placeholder="Select or type customer type"
                    loading={customerLookupLoading}
                    onChange={handleCustomerTypeChange}
                  />
                </Col>
                {(isServiceCustomer || isBankerCustomer) && (
                  <Col xs={12} sm={6}>
                    <FormikCreatableSelect
                      name="pdf_content.customer_info.designation"
                      label="Designation"
                      formik={formik}
                      options={designationSelectOptions}
                      placeholder="Select or type a designation"
                      loading={customerLookupLoading}
                    />
                  </Col>
                )}
                {(isServiceCustomer || isBankerCustomer) && (
                  <Col xs={12} sm={6}>
                    <FormikCreatableSelect
                      name={
                        isBankerCustomer
                          ? "pdf_content.customer_info.bank_name"
                          : "pdf_content.customer_info.company_name"
                      }
                      label={
                        selectedCustomerTypeMeta?.organizationLabel ||
                        getCustomerOrganizationLabel(selectedCustomerType)
                      }
                      formik={formik}
                      options={
                        isBankerCustomer ? bankSelectOptions : companySelectOptions
                      }
                      placeholder={`Select or type ${isBankerCustomer ? "bank" : "company"} name`}
                      loading={customerLookupLoading}
                    />
                  </Col>
                )}
                {isBankerCustomer && (
                  <Col xs={12} sm={6}>
                    <FormikCreatableSelect
                      name="pdf_content.customer_info.branch_name"
                      label="Branch Name"
                      formik={formik}
                      options={branchSelectOptions}
                      placeholder="Select or type branch name"
                      loading={customerLookupLoading}
                    />
                  </Col>
                )}
                <Col md={12}>
                  <Label className="form-label">Address</Label>
                  <Input
                    type="textarea"
                    rows={3}
                    name="pdf_content.customer_info.address"
                    value={
                      formik.values.pdf_content?.customer_info?.address || ""
                    }
                    onChange={formik.handleChange}
                    placeholder="Customer address shown in the PDF"
                  />
                </Col>
              </Row>
            </div>
          </Col>

          {/* ── VEHICLE SECTION ──────────────────────────────── */}
          <Col md={12}>
            <div className="section-header">
              <h6>VEHICLE INFORMATION</h6>
              <hr />
            </div>
          </Col>

          <Col md={12}>
            <div className="editor-panel">
              <Row className="g-3">
                <Col md={8}>
                  <Label className="form-label">Full Chassis</Label>
                  <Input
                    name="pdf_content.vehicle_info.full_chassis"
                    value={
                      formik.values.pdf_content?.vehicle_info?.full_chassis ||
                      ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={12}>
                  <Label className="form-label">Full Engine</Label>
                  <Input
                    name="pdf_content.vehicle_info.full_engine"
                    value={
                      formik.values.pdf_content?.vehicle_info?.full_engine || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Label className="form-label">Vehicle Name</Label>
                  <Input
                    name="pdf_content.vehicle_info.name"
                    value={
                      formik.values.pdf_content?.vehicle_info?.name || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Label className="form-label">Chassis No.</Label>
                  <Input
                    name="pdf_content.vehicle_info.chassis_no"
                    value={
                      formik.values.pdf_content?.vehicle_info?.chassis_no || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={6}>
                  <Label className="form-label">BRTA Reg.</Label>
                  <Input
                    name="pdf_content.vehicle_info.brta_reg"
                    value={
                      formik.values.pdf_content?.vehicle_info?.brta_reg || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={4}>
                  <Label className="form-label">Year/Model</Label>
                  <Input
                    name="pdf_content.vehicle_info.year_model"
                    value={
                      formik.values.pdf_content?.vehicle_info?.year_model || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={4}>
                  <Label className="form-label">Color</Label>
                  <Input
                    name="pdf_content.vehicle_info.color"
                    value={
                      formik.values.pdf_content?.vehicle_info?.color || ""
                    }
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={4}>
                  <Label className="form-label">CC</Label>
                  <Input
                    name="pdf_content.vehicle_info.cc"
                    value={formik.values.pdf_content?.vehicle_info?.cc || ""}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col md={4}>
                  <Label className="form-label">Fuel</Label>
                  <Input
                    name="pdf_content.vehicle_info.fuel"
                    value={formik.values.pdf_content?.vehicle_info?.fuel || ""}
                    onChange={formik.handleChange}
                  />
                </Col>
              </Row>
            </div>
          </Col>

          {/* ── PAYMENT SUMMARY ──────────────────────────────── */}
          <Col md={12}>
            <div className="section-header">
              <h6>PAYMENT SUMMARY</h6>
              <hr />
            </div>
          </Col>

          <Col md={4}>
            <Label className="form-label">Car Price</Label>
            <Input
              type="number"
              name="car_price"
              value={formik.values.car_price}
              onChange={formik.handleChange}
            />
          </Col>
          <Col md={4}>
            <Label className="form-label">Registration Cost</Label>
            <Input
              type="number"
              name="registration_cost"
              value={formik.values.registration_cost}
              onChange={formik.handleChange}
            />
          </Col>
          <Col md={4}>
            <Label className="form-label">Discount</Label>
            <Input
              type="number"
              name="pdf_content.vehicle_info.discount"
              value={formik.values.pdf_content?.vehicle_info?.discount ?? ""}
              onChange={formik.handleChange}
            />
          </Col>

          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Label className="form-label mb-0">Additional Costs</Label>
              <Button
                type="button"
                color="outline-primary"
                size="sm"
                onClick={() =>
                  formik.setFieldValue("pdf_content.additional_costs", [
                    ...additionalCosts,
                    createEmptyAdditionalCost(),
                  ])
                }
              >
                <i className="bx bx-plus me-1" />
                Add Cost
              </Button>
            </div>
            <Row className="g-2">
              {additionalCosts.map((item, index) => (
                <Col md={12} key={`cost-${index}`}>
                  <Row className="g-2 align-items-center">
                    <Col md={7}>
                      <Input
                        name={`pdf_content.additional_costs.${index}.name`}
                        value={item?.name ?? ""}
                        onChange={formik.handleChange}
                        placeholder="Cost name"
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        type="number"
                        name={`pdf_content.additional_costs.${index}.amount`}
                        value={item?.amount ?? ""}
                        onChange={formik.handleChange}
                        placeholder="Amount"
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        type="button"
                        color="light"
                        className="w-100"
                        onClick={() => {
                          const next = additionalCosts.filter(
                            (_, i) => i !== index
                          )
                          formik.setFieldValue(
                            "pdf_content.additional_costs",
                            next.length ? next : [createEmptyAdditionalCost()]
                          )
                        }}
                      >
                        <i className="bx bx-trash" />
                      </Button>
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
          </Col>

          <Col md={4}>
            <div className="total-section">
              {[
                { label: "Car Price", value: Number(formik.values.car_price || 0) },
                {
                  label: "Registration Cost",
                  value: Number(formik.values.registration_cost || 0),
                },
                ...additionalCosts.map((item, i) => ({
                  label: item?.name || `Additional Cost ${i + 1}`,
                  value: Number(item?.amount || 0),
                })),
                { label: "Total Amount", value: totalAmount, strong: true },
                { label: "Amount Paid", value: paymentsTotal },
                { label: "Overpaid", value: overpaidAmount },
                { label: "Due", value: dueAmount, danger: true },
              ]
                .filter(item => item.value > 0 || item.label === "Total Amount")
                .map(item => (
                  <div
                    key={item.label}
                    className={`d-flex justify-content-between gap-3 ${
                      item.strong
                        ? "fw-bold fs-6 border-top pt-2 mt-2"
                        : item.danger
                        ? "fw-bold text-danger border-top pt-2 mt-2"
                        : "mb-2"
                    }`}
                  >
                    <span>{item.label}</span>
                    <span>{formatAmount(item.value)}</span>
                  </div>
                ))}
            </div>
          </Col>

          {/* ── PAYMENTS ─────────────────────────────────────── */}
          <Col md={12}>
            <div className="section-header">
              <h6>PAYMENTS</h6>
              <hr />
            </div>
          </Col>

          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Label className="form-label mb-0">Payment Entries</Label>
              <Button
                type="button"
                color="outline-primary"
                size="sm"
                onClick={() =>
                  formik.setFieldValue("payments", [
                    ...payments,
                    createEmptyPayment(),
                  ])
                }
              >
                <i className="bx bx-plus me-1" />
                Add Payment
              </Button>
            </div>
            <Row className="g-2">
              <Col md={3}>
                <small className="text-muted fw-semibold">Date</small>
              </Col>
              <Col md={3}>
                <small className="text-muted fw-semibold">Method</small>
              </Col>
              <Col md={3}>
                <small className="text-muted fw-semibold">Amount</small>
              </Col>
              <Col md={2}>
                <small className="text-muted fw-semibold">Notes</small>
              </Col>
              <Col md={1} />
              {payments.map((payment, index) => (
                <Col md={12} key={`payment-${index}`}>
                  <Row className="g-2 align-items-center">
                    <Col md={3}>
                      <Input
                        type="date"
                        name={`payments.${index}.received_date`}
                        value={payment?.received_date ?? ""}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={3}>
                      <Input
                        name={`payments.${index}.payment_method`}
                        value={payment?.payment_method ?? ""}
                        onChange={formik.handleChange}
                        placeholder="e.g. Cash, Cheque"
                      />
                    </Col>
                    <Col md={3}>
                      <Input
                        type="number"
                        name={`payments.${index}.amount`}
                        value={payment?.amount ?? ""}
                        onChange={formik.handleChange}
                        placeholder="0"
                      />
                    </Col>
                    <Col md={2}>
                      <Input
                        name={`payments.${index}.notes`}
                        value={payment?.notes ?? ""}
                        onChange={formik.handleChange}
                        placeholder="Notes / Ref."
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        type="button"
                        color="light"
                        className="w-100"
                        onClick={() => {
                          const next = payments.filter((_, i) => i !== index)
                          formik.setFieldValue(
                            "payments",
                            next.length ? next : [createEmptyPayment()]
                          )
                        }}
                      >
                        <i className="bx bx-trash" />
                      </Button>
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
          </Col>

          {/* ── NOTES ────────────────────────────────────────── */}
          <Col md={12}>
            <div className="section-header">
              <h6>NOTES</h6>
              <hr />
            </div>
            <Input
              type="textarea"
              name="notes"
              rows={4}
              value={formik.values.notes}
              onChange={formik.handleChange}
              placeholder="Optional notes for the printed money receipt"
            />
          </Col>
        </Row>
      </Form>
    </ThemeModal>
  )
}

export default CustomReceiptModal
